const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestInitWithJson = Omit<RequestInit, 'body'> & {
  json?: unknown;
};

// Single in-flight refresh promise, shared across concurrent 401 retries so a
// burst of expired requests only triggers one /api/auth/refresh call.
let pendingRefresh: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Clear after a microtask so simultaneous callers all see the same promise
      // resolve, but the next round of expirations triggers a fresh refresh.
      queueMicrotask(() => {
        pendingRefresh = null;
      });
    }
  })();
  return pendingRefresh;
}

async function rawFetch(path: string, init: RequestInitWithJson): Promise<Response> {
  const headers = new Headers(init.headers);
  let body: BodyInit | null = (init as RequestInit).body ?? null;
  if (init.json !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(init.json);
  }
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    body,
    credentials: 'include',
  });
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInitWithJson = {},
): Promise<T> {
  let response = await rawFetch(path, init);
  // Transparent refresh: if the access cookie expired (1h TTL) but the refresh
  // cookie is still valid (30d TTL), POST /api/auth/refresh once and retry the
  // original request. Skip for /api/auth/* itself to avoid a loop.
  if (response.status === 401 && !path.startsWith('/api/auth/')) {
    const refreshed = await tryRefresh();
    if (refreshed) response = await rawFetch(path, init);
  }
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // Not JSON; expose raw text via ApiError if request failed.
    }
  }
  if (!response.ok) {
    // Hono's @hono/zod-validator returns `{ success: false, error: ZodError }`
    // on schema failures. Plain `String(zodError)` yields "[object Object]"
    // which surfaced to users as an opaque error. Detect that shape and turn
    // the first issue into a readable message — "gstin: Invalid GSTIN" beats
    // a meaningless object toString every time.
    const errCode = extractErrorCode(data, response.status);
    const message = extractErrorMessage(data) ?? (response.statusText || errCode);
    throw new ApiError(response.status, errCode, message);
  }
  return data as T;
}

function extractErrorCode(data: unknown, status: number): string {
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj['error'] === 'string') return obj['error'];
    if (obj['success'] === false && obj['error']) return 'validation_error';
  }
  return `http_${status}`;
}

function extractErrorMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  // Zod-validator failure shape.
  const err = obj['error'];
  if (err && typeof err === 'object' && Array.isArray((err as { issues?: unknown }).issues)) {
    const issues = (err as { issues: Array<unknown> }).issues;
    const first = issues[0];
    if (first && typeof first === 'object') {
      const issue = first as { path?: unknown; message?: unknown };
      const path = Array.isArray(issue.path) && issue.path.length > 0 ? issue.path.join('.') : '';
      const msg = typeof issue.message === 'string' ? issue.message : 'Invalid value';
      return path ? `${path}: ${msg}` : msg;
    }
  }
  // Hono/custom shape: { error: 'something' } — let extractErrorCode supply the code,
  // no need to repeat it as the message.
  return null;
}
